/**
 * This file contains functions that can be used in conjuncture with
 * `@shopify/restyle` to create components with responsive props
 * attached to our theme (like `backgroundColor` and `margin`).
 *
 * @see https://shopify.github.io/restyle/
 */

import React from 'react'
import {
  type ResponsiveValue as RestyleResponsiveValue,
  PropValue,
  useResponsiveProp as useRestyleResponsiveProp,
  useTheme as useRestyleTheme,
} from '@shopify/restyle'
import { type LightTheme } from '../themes'

/**
 * Returns the names of the theme breakpoints sorted by their pixel values.
 */
export const useSortedBreakpoints = () => {
  const theme = useRestyleTheme<LightTheme>()
  return React.useMemo(
    () =>
      Object.keys(theme.breakpoints).sort(
        (a, b) => (theme.breakpoints as any)[a] - (theme.breakpoints as any)[b],
      ),
    [theme.breakpoints],
  )
}

/**
 * Returns the name of the currently active breakpoint.
 */
export const useActiveBreakpoint = () => {
  const sortedBreakpoints = useSortedBreakpoints()

  return useRestyleResponsiveProp(
    sortedBreakpoints.reduce((acc, b) => ({ ...acc, [b]: b }), {} as any),
  ) as keyof LightTheme['breakpoints'] | undefined
}

/**
 * Returns a function that can be used to resolve a value from the theme.
 * The returned function handles responsive objects, theme token lookups,
 * and falls back to the original value if no theme match is found.
 *
 * @example
 * ```ts
 * const resolve = useThemeValueResolver()
 * const margin = resolve({ phone: 's', tablet: 'm' }, 'spacing')
 * ```
 */
export const useThemeValueResolver = () => {
  const theme = useRestyleTheme<LightTheme>()
  const activeBreakpoint = useActiveBreakpoint()
  const sortedBreakpoints = useSortedBreakpoints()

  return React.useCallback(
    (val: unknown, themeKey?: keyof LightTheme) => {
      let primitiveValue = val

      // Resolve responsive objects
      if (
        typeof val === 'object' &&
        val !== null &&
        !Array.isArray(val) &&
        activeBreakpoint
      ) {
        const activeIdx = sortedBreakpoints.indexOf(activeBreakpoint as string)
        primitiveValue = undefined
        // Find the highest breakpoint <= activeBreakpoint that has a value.
        for (let i = activeIdx; i >= 0; i--) {
          const b = sortedBreakpoints[i]
          if ((val as any)[b] !== undefined) {
            primitiveValue = (val as any)[b]
            break
          }
        }
      }

      // Resolve theme values if a themeKey is provided
      if (
        themeKey &&
        typeof primitiveValue === 'string' &&
        (theme[themeKey] as any)?.[primitiveValue] !== undefined
      ) {
        return (theme[themeKey] as any)[primitiveValue]
      }

      return primitiveValue
    },
    [theme, activeBreakpoint, sortedBreakpoints],
  )
}

/**
 *  Convert a union of possible values into their responsive equivalents.
 *
 * ```ts
 * type Foo = ResponsiveValue<'primary' | 'secondary'>
 * // Foo can now be 'primary' or { phone: 'primary' } etc
 * ````
 */
export type ResponsiveValue<ValuesUnion> = RestyleResponsiveValue<
  ValuesUnion,
  LightTheme['breakpoints']
>

/**
 * A type helper for creating an interface with responsive style properties.
 *
 * @example
 *
 * ```ts
 * type Borders = ResponsiveThemeProps<'borderWidth' | 'borderTopWidth', 's' | 'm'>
 *
 * // Results in an interface similar to:
 * interface Borders {
 *   borderWidth?    : ResponsiveValue<'s' | 'm', Theme['breakpoints']>
 *   borderTopWidth? : ResponsiveValue<'s' | 'm', Theme['breakpoints']>
 * }
 * ```
 */
export type ResponsiveThemeProps<
  /**
   * These will become the keys on the output interface
   */
  Props extends string | number | symbol,
  /**
   * The type of values allowed for each key
   */
  Values,
> = Partial<Record<Props, ResponsiveValue<Values>>>

export type UseResponsiveProp = <TVal extends PropValue>(
  propValue: ResponsiveValue<TVal>,
) => TVal | undefined
/**
 * Get a responsive prop value from the theme.
 */
export const useResponsiveProp = useRestyleResponsiveProp as UseResponsiveProp

type ThemeValue = any

interface TransfromFunctionArgs {
  value: ThemeValue
  theme: Record<string, Record<string, ThemeValue> | boolean>
}

/**
 * A helper to create a theme value lookup function that checks against multiple
 * keys in the theme.
 *
 * @example
 * ```ts
 * const theme = {
 *   foo: {
 *     light: 'white',
 *   },
 *   bar:
 *     dark: 'black
 *   }
 * }
 *
 * const transform = multiKeyTransformFactory(['foo', 'bar'])
 *
 * const color = transform({value: 'dark', theme})
 * // color -> 'black'
 * ```
 */
export const multiKeyTransformFactory = (keys: string[]) => {
  return (
    { value, theme }: TransfromFunctionArgs,
    /**
     * Prevent warnings if the theme key does not exist in the theme.
     */
    silent = false,
  ) => {
    if (value == null) return undefined
    const key = keys.find((k) => !!theme[k][value])
    if (key) return theme[key][value]
    if (!silent)
      console.error(
        `${String(
          value,
        )} is not defined in the theme under any of the following keys:`,
        keys,
      )
    return undefined
  }
}

/**
 * A helper to create the theme value lookup function that will fallback to the
 * value passed if it is not in the theme.
 */
export const themeOrValue = (key: string) => {
  return ({ value, theme }: TransfromFunctionArgs) => {
    if (value == null) return value
    return theme[key][value] || value
  }
}
